import { FC } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Redirect, useParams } from 'react-router-dom';
import { Flex } from '@rebass/emotion';
import { FormattedMessage } from 'react-intl';

import Loader from '@app/components/Loader';
import GenericError, {
  GenericErrorTitle,
  GenericErrorMessage,
} from '@app/components/GenericError/GenericError';
import Link from '@app/components/Link';

import { Button } from '@app/shop/components/Button';
import messages from './Response.messages';
import {
  CheckoutResponseQuery,
  CheckoutResponseQueryVariables,
} from './__generated__/CheckoutResponseQuery';

const Response: FC = () => {
  const { cartId, language } = useParams<{
    cartId: string;
    language: string;
  }>();
  const { data, loading, error } = useQuery<
    CheckoutResponseQuery,
    CheckoutResponseQueryVariables
  >(CHECKOUT_RESPONSE_QUERY, {
    variables: { cartId },
    fetchPolicy: 'network-only',
    ssr: false,
  });
  const { checkPlaceOrderStatus } = data?.magento2 ?? {};
  const orderErrors = checkPlaceOrderStatus?.errors;
  const orderNumber = checkPlaceOrderStatus?.orderIncrementId;

  if (loading) {
    return (
      <Flex justifyContent="center">
        <Loader />
      </Flex>
    );
  }

  // We redirect to Success Page whenever an order has been created and there are no errors.
  if (checkPlaceOrderStatus?.orderStatus && !(error || orderErrors?.length)) {
    return (
      <Redirect
        to={{ pathname: `/${language}/shop/checkout/success/${orderNumber}` }}
      />
    );
    // Otherwise we show the OrderStatusError component with instructions to the user.
  } else {
    return <OrderStatusError />;
  }
};

export default Response;

const OrderStatusError: FC = () => {
  return (
    <GenericError data-testid="response-page-error">
      <GenericErrorTitle>
        <FormattedMessage {...messages.headline} />
      </GenericErrorTitle>
      <GenericErrorMessage>
        <FormattedMessage {...messages.message} />
      </GenericErrorMessage>
      <Link to={'/:language/account/orders/'}>
        <Button type="primary">
          <FormattedMessage {...messages.orderHistory} />
        </Button>
      </Link>
    </GenericError>
  );
};

export const CHECKOUT_RESPONSE_QUERY = gql`
  query CheckoutResponseQuery($cartId: String!) {
    magento2 {
      checkPlaceOrderStatus(cart_id: $cartId) {
        orderIncrementId
        orderStatus
        errors
      }
    }
  }
`;
